import AppTheme from "@toothfairy/shared-ui/AppTheme";
import Charting from "../../../Components/Charting";
import Constants from "../../../Constants";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import requestLogsHelper from "../../../API/RequestLogs/requestLogsHelper";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import DashboardItem from "./DashboardItem";
import DashboardPlaceholder from "./DashboardPlaceholder";

// Bar chart of support hours consumed across the current billing cycle. Only
// rendered when the workspace has a support hours budget (same visibility rule
// as the Admin → Support screen). Support logs are aggregated per day over the
// billing period (workspace.billingPeriodStart -> billingPeriodEnd), falling
// back to the navigated month when no billing cycle is configured. The budget
// delta shown in the title is NOT scoped to the billing cycle: support hours
// are a budget, so the used figure is the sum of every support log associated
// with the workspace, matching the Admin → Support screen.
const SupportConsumptionChart = ({ billingPeriod, selectedDate }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const workspace = getActiveWorkspace();
  const hasSupportBudget = (workspace?.supportHours || 0) > 0;
  const [logs, setLogs] = useState([]);
  const [isFetchInProgress, setIsFetchInProgress] = useState(true);

  const loadLogs = useCallback(async () => {
    if (!workspace?.id) return;
    setIsFetchInProgress(true);
    try {
      const result = await requestLogsHelper.getAllRequestLogsByWorkspaceAndType(
        workspace.id,
        "support",
      );
      setLogs(result || []);
    } catch (error) {
      console.error("An error occurred while loading support logs", error);
    } finally {
      setIsFetchInProgress(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    if (hasSupportBudget) loadLogs();
  }, [hasSupportBudget, loadLogs]);

  if (!hasSupportBudget || !workspace) return null;

  const startDay = billingPeriod?.billingPeriodStart
    ? dayjs(billingPeriod.billingPeriodStart).startOf("day")
    : selectedDate?.startOf("month");
  const endDay = billingPeriod?.billingPeriodEnd
    ? dayjs(billingPeriod.billingPeriodEnd).endOf("day")
    : selectedDate?.endOf("month");

  if (!startDay.isValid() || !endDay.isValid()) return null;

  const totalDays =
    endDay.startOf("day").diff(startDay.startOf("day"), "day") + 1;
  if (totalDays <= 0 || totalDays > 62) return null;

  const dayMap = {};
  logs.forEach((log) => {
    if (!log?.creationTime) return;
    const d = dayjs(log.creationTime * 1000);
    if (d.isBefore(startDay) || d.isAfter(endDay)) return;
    const key = d.format("YYYY-MM-DD");
    dayMap[key] = (dayMap[key] || 0) + (log?.supportHours || 0);
  });

  const labels = [];
  const data = [];
  for (let i = 0; i < totalDays; i += 1) {
    const d = startDay.add(i, "day");
    labels.push(d.format("DD"));
    data.push(dayMap[d.format("YYYY-MM-DD")] || 0);
  }

  const totalUsed = data.reduce((sum, value) => sum + value, 0);
  const hasData = totalUsed > 0;
  const usedHours = logs.reduce(
    (sum, log) => sum + (log?.supportHours || 0),
    0,
  );

  return (
    <DashboardItem
      isFetchInProgress={isFetchInProgress}
      title={t("dashboard.supportConsumption.title")}
      isHalfWidth={false}
      additionalTitle={` ${usedHours} / ${workspace?.supportHours || 0} ${t(
        "dashboard.supportConsumption.hoursUnit",
      )}`}
      additionalTitleWrapperStyle={{
        marginTop: 3,
      }}
    >
      {hasData ? (
        <Charting.AppBarChart
          maxHeight={Constants.chartMaxHeight}
          options={{
            legend: {
              display: false,
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${context.parsed.y} ${t(
                      "dashboard.supportConsumption.hoursUnit",
                    )}`,
                },
              },
            },
            scales: {
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: currentTheme?.theme?.font_color,
                  font: {
                    size: currentTheme?.theme?.extra_small_font_size,
                  },
                },
              },
              y: {
                type: "linear",
                display: true,
                position: "left",
                beginAtZero: true,
                ticks: {
                  color: currentTheme?.theme?.font_color,
                  font: {
                    size: currentTheme?.theme?.extra_small_font_size,
                  },
                },
              },
            },
          }}
          dataset={{
            labels,
            datasets: [
              {
                label: t("dashboard.supportConsumption.used"),
                data,
                borderColor: currentTheme?.theme?.primary,
                borderRadius: 6,
                backgroundColor: currentTheme?.theme?.primary,
                yAxisID: "y",
              },
            ],
          }}
        />
      ) : (
        <DashboardPlaceholder
          mainCaption={t("dashboard.supportConsumption.emptyPlaceholder")}
          subCaption={t("dashboard.supportConsumption.emptySubtitle")}
        />
      )}
    </DashboardItem>
  );
};

export default SupportConsumptionChart;
